import { useMemo } from 'react';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { PILLARS } from '../lib/pillars';

export const ExperienceMap = ({ profile, scores }) => {
  const isSeeking = profile.mode === 'seeking';

  const data = useMemo(() => {
    if (!profile?.drivers) return [];
    return PILLARS.map(pillar => {
      const importance = profile.drivers[pillar.id]?.importance || 0;
      const pillarScore = scores?.pillarScores?.find(s => s.pillarId === pillar.id);
      return {
        pillar: pillar.shortName,
        importance,
        availability: isSeeking ? 0 : (profile.availability || 0),
        effectiveAccess: pillarScore?.effectiveAccess || 0,
        isAligned: pillarScore?.isAligned || false
      };
    });
  }, [profile, scores, isSeeking]);

  if (data.length === 0) return null;

  return (
    <Card className="border-border bg-card" data-testid="experience-map">
      <CardHeader>
        <CardTitle className="text-xl font-light">Experience Map</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          {isSeeking ? 'What matters most to you' : 'What shapes the experience for you'}
        </p>
      </CardHeader>
      <CardContent>
        <div className="w-full" style={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={data} cx="50%" cy="50%" outerRadius="75%">
              <PolarGrid
                stroke="hsl(var(--border))"
                strokeOpacity={0.5}
              />
              <PolarAngleAxis
                dataKey="pillar"
                tick={{ fill: 'hsl(var(--foreground))', fontSize: 12, fontWeight: 300 }}
              />

              {/* Baseline (current mode only) */}
              {!isSeeking && (
                <Radar
                  name="Presence"
                  dataKey="availability"
                  stroke="hsl(var(--muted-foreground))"
                  strokeWidth={1}
                  strokeDasharray="4 4"
                  fill="hsl(var(--muted-foreground))"
                  fillOpacity={0.05}
                />
              )}

              {/* Importance (what you need) */}
              <Radar
                name="Importance"
                dataKey="importance"
                stroke="hsl(var(--teal))"
                strokeWidth={2}
                fill="hsl(var(--teal))"
                fillOpacity={0.15}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-[hsl(var(--teal))] rounded" />
            <span>{isSeeking ? 'What matters to you' : 'What you need'}</span>
          </div>
          {!isSeeking && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-0.5 border-t border-dashed border-muted-foreground" />
              <span>How it tends to show up</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
